import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Divider, Input, InputRef, Modal, Row, Space } from "antd";
import { ColumnsType, ColumnType } from "antd/lib/table";
import { SearchOutlined } from "@ant-design/icons";
import { FilterConfirmProps } from "antd/lib/table/interface";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { AlertType, useAlert } from "../provider/AlertProvider";

import * as Request from "../commons/request";
import * as Types from "../commons/typeDTO";
import * as String from "../commons/string";
import * as TypeUtils from "../commons/typeUtils";

import DataTable from "../components/DataTable";
import RegisterColor from "../components/RegisterColor";
import Highlighter from "react-highlight-words";

type DataIndex = keyof Types.ColorDto;

function ColorList() {
    const alert = useAlert();
    const searchInput = useRef<InputRef>(null);

    const [colors, setColors] = useState<Array<Types.ColorDto>>();
    const [currentColor, setcurrentColor] = useState<Types.ColorDto>();

    const [editMode, setEditMode] = useState<TypeUtils.editMode>("none");
    const [openDeleteColor, setOpenDeleteColor] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");

    useEffect(() => {
        requestGetColors();
    }, []);

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<Types.ColorDto> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => onSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => onSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        {String.search}
                    </Button>
                    <Button onClick={() => clearFilters && onReset(clearFilters)} size="small" style={{ width: 90 }}>
                        {String.reset}
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        {String.filter}
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        {String.close}
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<Types.ColorDto> = [
        {
            align: "center",
            title: String.number,
            dataIndex: "num",
            width: "6%",
            render: (item, record, index) => <>{colors && colors.indexOf(record) + 1}</>,
        },
        {
            align: "center",
            title: String.colorId,
            dataIndex: "colorId",
            key: "colorId",
            className: "hidden-column",
            ...getColumnSearchProps("colorId"),
        },
        {
            align: "center",
            title: String.colorName,
            dataIndex: "colorName",
            key: "colorName",
            ...getColumnSearchProps("colorName"),
        },
    ];

    //get colors
    const requestAxiosGetColors = async () => {
        const response = await utilAxiosWithAuth().get(Request.COLOR_LIST_URL);
        console.log(response.data);
        return response.data;
    };

    const {
        loading: loadingGetColors,
        error: errorGetColors,
        data: resultGetColors,
        execute: requestGetColors,
    } = useAsyncAxios(requestAxiosGetColors);

    useEffect(() => {
        if (resultGetColors === null) return;

        setColors(resultGetColors.colors);
    }, [resultGetColors]);

    useEffect(() => {
        if (errorGetColors === null) return;

        alert.setAlert(errorGetColors.response.data.code, "Error", errorGetColors.response.data.message);
    }, [errorGetColors]);

    //remove color
    const requestAxiosRemoveColor = async (color: Types.ColorDto) => {
        const response = await utilAxiosWithAuth().delete(Request.COLOR_URL + "/" + color?.colorId);
        return response.data;
    };

    const {
        loading: loadingRemoveColor,
        error: errorRemoveColor,
        data: resultRemoveColor,
        execute: requestRemoveColors,
    } = useAsyncAxios(requestAxiosRemoveColor);

    useEffect(() => {
        if (resultRemoveColor === null) return;

        requestGetColors();
        alert.setAlert(AlertType.SUCCESS, String.color + String.removeSuccsee, String.color + String.msgRemoveSuccess);
    }, [resultRemoveColor]);

    useEffect(() => {
        if (errorRemoveColor === null) return;

        alert.setAlert(errorRemoveColor.response.data.code, String.color + String.removeError, errorRemoveColor.response.data.message);
    }, [errorGetColors]);

    const onRow = (value: Types.ColorDto) => {
        setEditMode("none");
        setcurrentColor(value);
    };

    const onDeleteColor = () => {
        if (currentColor !== undefined) setOpenDeleteColor(true);
    };

    const onDeleteColorOk = () => {
        setOpenDeleteColor(false);
        requestRemoveColors(currentColor);
    };

    const onDeleteColorCancel = () => {
        setOpenDeleteColor(false);
    };

    const onRegisterColor = () => {
        requestGetColors();
        setEditMode("none");
    };

    const onSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const onReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    return (
        <>
            <Row justify={"space-between"}>
                <Col span={6}>
                    <Divider orientation="left">
                        <span>{String.color}</span>
                    </Divider>
                </Col>
                <Space>
                    <Button
                        onClick={() => {
                            editMode === "add" ? setEditMode("none") : setEditMode("add");
                        }}
                    >
                        {String.userAdd}
                    </Button>
                    <Button
                        onClick={() => {
                            editMode === "update" ? setEditMode("none") : setEditMode("update");
                        }}
                    >
                        {String.userUpdate}
                    </Button>
                    <Button onClick={() => onDeleteColor()} danger>
                        {String.userRemove}
                    </Button>
                </Space>
            </Row>
            <DataTable hasScroll={false} columns={columns} rowKey="colorId" dataSource={colors} onRow={onRow} />

            {editMode === "add" && <RegisterColor editMode={editMode} onRegisterColor={onRegisterColor} />}
            {editMode !== "add" && currentColor !== undefined && (
                <RegisterColor editMode={editMode} color={currentColor} onRegisterColor={onRegisterColor} />
            )}

            {openDeleteColor && (
                <Modal open={openDeleteColor} onOk={onDeleteColorOk} onCancel={onDeleteColorCancel}>
                    {currentColor?.colorName} 삭제하시겠습니까?
                </Modal>
            )}
        </>
    );
}

export default ColorList;
